import { useState, useEffect } from "react";
import { Menu, X, MessageCircle, ShoppingBag } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSettings, buildWhatsAppLink } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";
import CurrencySwitcher from "@/components/CurrencySwitcher";

const NAV = [
  { label: "Collections", href: "#collections" },
  { label: "Customers", href: "#customers" },
  { label: "How it Works", href: "#how" },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const { settings } = useSettings();
  const { totalQuantity, openCart } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waLink = buildWhatsAppLink(settings);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-md border-b border-[#2A2A2A]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="header-logo-link" className="flex items-center">
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-9" data-testid="header-desktop-nav">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-sm tracking-[0.16em] uppercase text-white/75 hover:text-[#D4AF37] transition-colors"
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <CurrencySwitcher variant="header" />
          <button
            onClick={openCart}
            className="relative w-10 h-10 border border-[#2A2A2A] hover:border-[#D4AF37] text-white/80 hover:text-[#D4AF37] flex items-center justify-center transition-colors"
            aria-label={`Open cart (${totalQuantity} items)`}
            data-testid="header-cart-button"
          >
            <ShoppingBag className="w-4 h-4" />
            {totalQuantity > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[0.6rem] font-bold flex items-center justify-center">
                {totalQuantity}
              </span>
            )}
          </button>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="btn-whatsapp text-sm"
            data-testid="header-whatsapp-button"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="hidden xl:inline">WhatsApp Us</span>
            <span className="xl:hidden">WhatsApp</span>
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={openCart}
            className="relative w-10 h-10 text-white/80 flex items-center justify-center"
            aria-label={`Open cart (${totalQuantity} items)`}
            data-testid="header-cart-button-mobile"
          >
            <ShoppingBag className="w-5 h-5" />
            {totalQuantity > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#D4AF37] text-black text-[0.6rem] font-bold flex items-center justify-center">
                {totalQuantity}
              </span>
            )}
          </button>
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            className="text-white p-2 -mr-2"
            data-testid="header-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/97 backdrop-blur-md flex flex-col" data-testid="mobile-menu">
          <div className="flex items-center justify-between h-16 px-5 border-b border-[#2A2A2A]">
            <Logo />
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="text-white p-2 -mr-2"
              data-testid="header-menu-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 flex flex-col p-8 gap-6">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-2xl font-serif text-white hover:text-[#D4AF37] transition"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
            <div className="mt-6 gold-hairline" />
            <CurrencySwitcher variant="mobile" />
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp justify-center mt-4"
              data-testid="mobile-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <p className="text-white/50 text-sm mt-2 text-center">
              {settings?.whatsapp_number}
            </p>
          </nav>
        </div>
      )}
    </header>
  );
}
